<template>
  <PapaAutoForm
    id="cardForm"
    v-model="join.card"
    @submit="submitCard"
    @update:model-value="updateValue"
    :fields="fields"
  >
    <template #address="{ field, updateField }">
      <label v-if="field.label !== false">{{
        field.label || field.name
      }}</label>
      <label class="flex-row flex-align-center gap-sm p-2xs-y checkbox">
        <Checkbox v-model="sameAs" binary />
        <div>Same as Shipping</div>
      </label>
      <div v-if="sameAs">
        <AddressBlock
          v-if="join.card.billing_address"
          :value="join.card.billing_address"
        />
      </div>
      <div v-else>
        <AddressForm
          v-model="billingAddress"
          :billing="false"
          :shipping="false"
        />
      </div>
      <div></div>
    </template>
    <template #default>
      <slot />
    </template>
  </PapaAutoForm>
</template>

<script setup lang="ts">
import { useJoinStore } from "@/stores/join";
import { blankAddress } from "@/utils/utilities";
import type { Address } from "@paparazzi/types/paparazzi.api.serializers";
import copy from "@virgodev/bazaar/functions/copy";
import type { FieldDef } from "@virgodev/vue-models/components/forms/defs";
import Checkbox from "primevue/checkbox";
import { computed, onMounted, ref, watch } from "vue";
import PapaAutoForm from "../PapaAutoForm.vue";
import AddressBlock from "../addresses/AddressBlock.vue";
import AddressForm from "../addresses/AddressForm.vue";

const emits = defineEmits(["submit"]);
const billingAddress = ref<Address>(blankAddress());
const sameAs = ref<boolean>(true);
const join = useJoinStore();

const fields = computed<FieldDef[]>(() => {
  const retval = [
    {
      type: "sub",
      name: "sub-row",
      css_classes: "hrow",
      label: false,
      fields: [
        {
          name: "first_name",
          label: "First Name",
          autocomplete: "cc-given-name",
        },
        {
          name: "last_name",
          label: "Last Name",
          autocomplete: "cc-family-name",
        },
      ],
    },
    {
      type: "ccnum",
      name: "card",
      label: "Credit Card Number",
    },
    {
      type: "sub",
      name: "sub-row",
      css_classes: "hrow",
      label: false,
      fields: [
        {
          type: "masked",
          name: "exp_date",
          label: "Expiration Date",
          extra: { mask: "99/99", slotChar: "MM/YY" },
          placeholder: "MM/YY",
          autocomplete: "cc-exp",
          css_classes: "_lr-hide",
        },
        {
          name: "cvc",
          css_classes: "_lr-hide",
          autocomplete: "cc-csc",
          minlength: 3,
          maxlength: 5,
        },
      ],
    },
    {
      type: "address",
      name: "billing_address",
      label: false,
    },
  ] as FieldDef[];
  return retval;
});

watch(
  [sameAs, billingAddress],
  () => {
    if (sameAs.value) {
      join.card.billing_address = copy(join.addressData);
    } else {
      join.card.billing_address = copy(billingAddress.value);
    }
  },
  { deep: true },
);

onMounted(() => {
  if (!join.card.billing_address) {
    join.card.billing_address = join.addressData;
  }
});

function submitCard(data: Record<string, any>) {
  join.cvc = data.cvc;
  emits("submit", join.card);
}

function updateValue(data: Record<string, any>) {
  join.cvc = data.cvc;
  if (data.exp_date) {
    [join.card.month, join.card.year] = data.exp_date.split("/");
  }
}
</script>

<style scoped>
#app .auto-form :deep(.hrow .inside) {
  grid-template-columns: 1fr 1fr;
}
#app .auto-form :deep(.hrow .inside input) {
  width: 100%;
}

#app .auto-form :deep(.checkboxes) {
  .inside {
    grid-template-columns: 1fr;
  }
}

#app .auto-form :deep(.checkboxes .inside) {
  grid-template-columns: max-content;
  gap: inherit;
}

#app .auto-form :deep(.checkboxes .inside label) {
  text-transform: capitalize !important;
}
label.checkbox {
  font-weight: 400 !important;
  text-transform: capitalize !important;
  line-height: 1 !important;
}
</style>
